import { verify_facial_verification_session } from '@/services/identity_facial_verification_service';
import {
  CAMERA_CONSTRAINT_OPTIONS,
  as_record,
  get_error_message,
  normalize_result
} from './facial_verification_field_shared';
import {
  capture_challenges,
  perform_countdown
} from './facial_verification_field_camera_helpers';

function get_camera_error_message(error) {
  const error_record = as_record(error);
  const message = error_record?.name === 'NotAllowedError'
    ? 'Camera permission was denied. Allow camera access and try again.'
    : get_error_message(error, 'The camera could not start: Unknown error');
  return message;
}

function request_camera_stream(state) {
  let last_error = null;

  function try_constraints(index) {
    if (index >= CAMERA_CONSTRAINT_OPTIONS.length) {
      state.local_error.value = get_camera_error_message(last_error);
      return Promise.resolve(null);
    }
    return navigator.mediaDevices
      .getUserMedia(Reflect.get(CAMERA_CONSTRAINT_OPTIONS, index))
      .then((stream) => stream)
      .catch((error) => {
        last_error = error;
        return try_constraints(index + 1);
      });
  }
  return try_constraints(0);
}

function stop_camera_stream(state) {
  if (state.camera_stream.value?.getTracks) {
    state.camera_stream.value.getTracks().forEach((track) => {
      track.stop();
    });
  }
  state.camera_stream.value = null;
  state.camera_ready.value = false;
}

function prepare_video_element(video, stream) {
  if (!video) {
    return false;
  }
  video.srcObject = stream;
  video.playsInline = true;
  video.muted = true;
  video.setAttribute('playsinline', 'true');

  return true;
}

async function start_camera_stream(state) {
  stop_camera_stream(state);

  const stream = await request_camera_stream(state);
  if (!stream) {
    state.status_message.value = 'Camera could not be started';
    return;
  }
  state.camera_stream.value = stream;
  const has_video = prepare_video_element(state.video.value, stream);
  if (!has_video) {
    return;
  }

  await state.video.value.play();
  state.camera_ready.value = true;
  state.status_message.value = 'Camera ready';
}

function canvas_to_data_url(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('The browser could not encode a camera frame.'));
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const result = typeof reader.result === 'string' ? reader.result : null;
          if (result) {
            resolve(result);
            return;
          }

          reject(new Error('The browser could not read a camera frame.'));
        };
        reader.onerror = () => reject(new Error('The browser could not read a camera frame.'));
        reader.readAsDataURL(blob);
      },
      'image/jpeg',
      0.78
    );
  });
}

async function capture_frame(state, action_code, capture_started_at) {
  if (!state.video.value?.videoWidth) {
    return null;
  }
  const source_width = state.video.value.videoWidth;
  const source_height = state.video.value.videoHeight;
  const width = Math.min(640, source_width);
  const height = Math.round(width * (source_height / source_width));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { alpha: false });
  if (!context) {
    return null;
  }

  context.translate(width, 0);
  context.scale(-1, 1);
  context.drawImage(state.video.value, 0, 0, width, height);
  const image_data = await canvas_to_data_url(canvas);
  const frame = {
    captured_at_ms: Math.round(performance.now() - capture_started_at),
    action: action_code,
    image_data
  };

  return frame;
}

function create_verification_payload(state) {
  const payload = {
    session_id: state.session.value.session_id,
    session_token: state.session.value.token,
    frames: state.frames.value,
    client: {
      user_agent: navigator.userAgent,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      camera_width: state.video.value?.videoWidth || null,
      camera_height: state.video.value?.videoHeight || null
    }
  };

  return payload;
}

async function submit_capture(state) {
  state.current_challenge.value = null;
  state.status_message.value = 'Analyzing the live capture...';
  state.frames.value.sort((left, right) => left.captured_at_ms - right.captured_at_ms);

  const payload = create_verification_payload(state);
  const verification_result = await verify_facial_verification_session(payload);
  const result = normalize_result(verification_result);

  return result;
}

function reset_capture_state(state) {
  state.current_challenge.value = null;
  state.challenge_index.value = -1;
  state.loading.value = false;
}

async function run_capture_flow(state, emitters) {
  await perform_countdown(state);
  await capture_challenges(state, capture_frame);

  state.result.value = await submit_capture(state);
  state.step.value = 4;
  state.status_message.value = 'Verification complete';
  stop_camera_stream(state);
  emitters.emit_value();
}

async function start_camera_action(state) {
  state.local_error.value = '';
  if (!navigator.mediaDevices?.getUserMedia) {
    state.local_error.value = 'This browser does not support camera capture.';
    return;
  }

  state.loading.value = true;
  try {
    await start_camera_stream(state);
  } catch (error) {
    stop_camera_stream(state);
    state.local_error.value = get_camera_error_message(error);
  } finally {
    state.loading.value = false;
  }
}

async function run_capture_action(state, emitters) {
  const has_session = Boolean(
    state.camera_ready.value
    && state.session.value?.session_id
    && state.session.value?.token
  );
  if (!has_session) {
    return;
  }

  state.step.value = 3;
  state.loading.value = true;
  state.local_error.value = '';
  state.frames.value = [];

  try {
    await run_capture_flow(state, emitters);
  } catch (error) {
    state.local_error.value = get_error_message(error, 'The live capture could not be verified.');
    state.step.value = 2;
    state.status_message.value = 'Capture needs to be repeated';
  } finally {
    reset_capture_state(state);
  }
}

function create_stop_camera_action(state) {
  function stop_camera() {
    stop_camera_stream(state);
  }
  return stop_camera;
}

export function create_camera_actions(state, emitters) {
  const start_camera = start_camera_action.bind(null, state);
  const run_capture = run_capture_action.bind(null, state, emitters);
  const stop_camera = create_stop_camera_action(state);
  const camera_actions = { start_camera, run_capture, stop_camera };
  return camera_actions;
}
